/**
 * NiV Clinic Detail Page — shell (header, quick actions, tabs, tab switch).
 *
 * Source-of-truth architecture:
 * - Primary: clinics collection + monthlyUsage subcollection
 * - Manual layer: niv_clinic_crm via clinic.nivCrm
 * - Activity: per-clinic 30-day series + 6-month money (getNivClinicActivitySeries)
 * - Telemetry: niv_telemetry_daily_therapist (shared query with the telemetry tab)
 *
 * Tabs: פרטי הקליניקה · ניהול · לני
 * Deep links pass location.state.tab (legacy overview/usage/telemetry/clinic-info → details).
 */

import { useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowRight, Buildings, User, Envelope, Prohibit, Warning } from "@phosphor-icons/react";
import {
  useGetNivClinicFullDetailQuery,
  useGetNivClinicActivitySeriesQuery,
  useGetClinicTelemetryDetailQuery,
  useUpdateClinicFieldMutation,
  useSetClinicPlanEndDateMutation,
  useDeleteClinicHardMutation,
} from "../../../../store/services/nivApi";
import { selectTherapistName } from "../../../../store/slices/authSlice";
import { useToast } from "../../../../contexts/ToastContext";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import PhoneNumber from "../../../../components/ui/PhoneNumber";
import ClinicTags from "../shared/ClinicTags";
import { suggestPlanEndAfterPayment } from "../shared/nivDerived";
import { TABS, resolveInitialTab, formatDate, toIsraelDateKey } from "./clinicDetailConstants";
import QuickActionsBar from "./components/QuickActionsBar";
import OverrideFlag from "./components/OverrideFlag";
import { PageSkeleton } from "./components/Skeletons";
import ClinicDetailsTab from "./tabs/ClinicDetailsTab";
import ManageTab from "./tabs/ManageTab";
import LennyTab from "./tabs/LennyTab";
import "./NivClinicDetailPage.css";

function NivClinicDetailPage() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const therapistName = useSelector(selectTherapistName);

  const [activeTab, setActiveTab] = useState(() => resolveInitialTab(location.state?.tab));
  const [confirm, setConfirm] = useState(null); // { kind: "inactive" | "extend", ... }
  const [focusPaymentToken, setFocusPaymentToken] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState("");
  const deletingRef = useRef(false);

  // Queries — the telemetry args match TelemetryTab so both share one cache entry
  const { data: clinic, isLoading, isFetching, refetch } = useGetNivClinicFullDetailQuery({ clinicId }, { skip: !clinicId });
  const { data: activity, isLoading: activityLoading } = useGetNivClinicActivitySeriesQuery({ clinicId }, { skip: !clinicId });
  const { data: telemetry, isLoading: telemetryLoading } = useGetClinicTelemetryDetailQuery(
    { clinicId, days: 90 },
    { skip: !clinicId }
  );

  const [updateClinicField, { isLoading: savingClinic }] = useUpdateClinicFieldMutation();
  const [setPlanEndDate, { isLoading: savingEnd }] = useSetClinicPlanEndDateMutation();
  const [deleteClinicHard, { isLoading: deleting }] = useDeleteClinicHardMutation();

  const crm = clinic?.nivCrm || null;

  // ============================================
  // HANDLERS
  // ============================================

  const handleBack = () => navigate("/dashboard/niv/clinics");

  const handleCopyId = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(clinicId);
      showSuccess("Clinic ID הועתק");
    } catch (error) {
      console.error("[CK-7999] Error copying clinic id:", {
        errorCode: "CK-7999",
        errorMessage: error?.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("לא ניתן להעתיק — העתק ידנית מלשונית פרטי קליניקה");
    }
  };

  // Disable / re-enable the clinic (inactiveByAdmin). Disabled clinics are
  // blocked from the app entirely and hidden from all NiV lists and stats.
  const handleToggleInactive = async () => {
    if (!clinic) return;
    const disabling = !clinic.inactiveByAdmin;
    try {
      await updateClinicField({
        clinicId: clinic.clinicId,
        fieldUpdates: disabling
          ? { inactiveByAdmin: true, inactiveByAdminAt: new Date().toISOString() }
          : { inactiveByAdmin: false },
        skipTimestamp: true, // don't bump updatedAt (activity proxy)
      }).unwrap();
      refetch();
      showSuccess(disabling ? "הקליניקה הושבתה" : "הקליניקה הופעלה מחדש");
    } catch (error) {
      console.error("[CK-7303] Error toggling clinic inactiveByAdmin:", {
        errorCode: "CK-7303",
        errorMessage: error?.message,
        clinicId: clinic.clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון סטטוס הקליניקה");
    }
  };

  // "+1 חודש" (one-time deals only): extend "פעיל עד" by one month from max(current, today)
  const handleExtendMonth = async (newDate) => {
    if (!clinic) return;
    try {
      await setPlanEndDate({ clinicId: clinic.clinicId, planEndDate: newDate }).unwrap();
      showSuccess(`״פעיל עד״ עודכן ל־${formatDate(newDate)}`);
    } catch (error) {
      console.error("[CK-7998] Error extending plan end date:", {
        errorCode: "CK-7998",
        errorMessage: error?.message,
        clinicId: clinic.clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהארכת המנוי");
    }
  };

  const openInactiveConfirm = () => {
    if (!clinic) return;
    const disabling = !clinic.inactiveByAdmin;
    setConfirm({
      kind: "inactive",
      title: disabling ? "השבתת קליניקה" : "הפעלה מחדש",
      message: disabling
        ? `להשבית את הקליניקה "${clinic.name || clinic.clinicId}"? המשתמשים שלה ייחסמו מהמערכת ויתבקשו ליצור קשר עם המשרד.`
        : `להפעיל מחדש את הקליניקה "${clinic.name || clinic.clinicId}"?`,
      confirmText: disabling ? "השבת" : "הפעל מחדש",
      type: disabling ? "danger" : "info",
    });
  };

  const openExtendConfirm = () => {
    if (!clinic) return;
    const newDate = suggestPlanEndAfterPayment(clinic.planEndDate, toIsraelDateKey(new Date()));
    setConfirm({
      kind: "extend",
      newDate,
      title: "הארכת ״פעיל עד״ בחודש",
      message: `לעדכן ״פעיל עד״ ל־${formatDate(newDate)}?${clinic.planEndDate ? ` (כיום: ${formatDate(clinic.planEndDate)})` : ""}`,
      confirmText: "הארך",
      type: "info",
    });
  };

  const handleLogPayment = () => {
    setActiveTab("manage");
    setFocusPaymentToken((t) => t + 1);
  };

  // Hard delete (test-clinic cleanup). Irreversible — the server removes the
  // clinic tree, its therapists + Auth users, NiV side-tables and Storage.
  const deleteTargetName = (clinic?.name || "").trim();
  const deleteNameMatches = deleteTargetName.length > 0 && deleteTyped.trim() === deleteTargetName;

  const openDeleteConfirm = () => {
    if (!clinic) return;
    setDeleteTyped("");
    setDeleteOpen(true);
  };

  // ConfirmDialog calls onClose right after onConfirm (sync) — the ref keeps
  // the dialog open while the server call is in flight.
  const closeDeleteConfirm = () => {
    if (deletingRef.current) return;
    setDeleteOpen(false);
    setDeleteTyped("");
  };

  const handleDeleteClinic = async () => {
    if (!clinic || !deleteNameMatches || deletingRef.current) return;
    deletingRef.current = true;
    try {
      const res = await deleteClinicHard({ clinicId: clinic.clinicId, confirmName: deleteTyped.trim() }).unwrap();
      deletingRef.current = false;
      showSuccess(`הקליניקה נמחקה לצמיתות (${res?.therapistsDeleted ?? 0} מטפלים הוסרו)`);
      setDeleteOpen(false);
      navigate("/dashboard/niv/clinics", { replace: true });
    } catch (error) {
      deletingRef.current = false;
      console.error("[CK-7966] Error hard-deleting clinic:", {
        errorCode: "CK-7966",
        errorMessage: error?.message,
        clinicId: clinic.clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("מחיקת הקליניקה נכשלה — בדוק את לוג הפונקציה (nivDeleteClinic)");
    }
  };

  // ============================================
  // LOADING / ERROR STATES
  // ============================================

  if (isLoading) return <PageSkeleton />;

  if (!clinic) {
    return (
      <div className="ncd2-page">
        <div className="ncd2-card ncd2-not-found">
          <Warning size={40} weight="fill" />
          <h2>הקליניקה לא נמצאה</h2>
          <button type="button" className="ncd2-btn ncd2-btn-primary" onClick={handleBack}>
            חזרה לרשימת הקליניקות
          </button>
        </div>
      </div>
    );
  }

  const tagsRow = { ...clinic, crm: crm || { paymentStatus: "not_paying" } };
  const diffs = clinic.limitDiffs || [];
  const busy = savingClinic || savingEnd || isFetching;

  return (
    <div className={`ncd2-page ncd2-tab-${activeTab}`}>
      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <header className="ncd2-header">
        <button type="button" className="ncd2-back" onClick={handleBack} aria-label="חזרה לרשימת הקליניקות">
          <ArrowRight size={18} weight="bold" />
          <span>חזרה</span>
        </button>

        <div className="ncd2-header-main">
          <div className={`ncd2-avatar${clinic.inactiveByAdmin ? " is-inactive" : ""}`}>
            {clinic.logoUrl ? <img src={clinic.logoUrl} alt="" /> : <Buildings size={20} weight="fill" />}
          </div>
          <div className="ncd2-header-info">
            <h1 className="ncd2-title">
              <span className="ncd2-title-text">{clinic.name || "ללא שם"}</span>
              {clinic.inactiveByAdmin && (
                <span className="ncd2-badge ncd2-badge-danger">
                  <Prohibit size={12} weight="bold" /> לא פעילה
                </span>
              )}
              <OverrideFlag diffs={diffs} label={`חריגות מתוכנית: ${diffs.length}`} className="ncd2-flag-badge" />
            </h1>
            <div className="ncd2-header-meta">
              <ClinicTags row={tagsRow} compact />
              <span className="ncd2-header-contact">
                {clinic.ownerName && (
                  <span className="ncd2-contact">
                    <User size={13} />
                    {clinic.ownerName}
                  </span>
                )}
                {clinic.ownerPhone && <PhoneNumber phone={clinic.ownerPhone} className="ncd2-contact is-link" iconSize={13} />}
                {clinic.ownerEmail && (
                  <a href={`mailto:${clinic.ownerEmail}`} className="ncd2-contact is-link">
                    <Envelope size={13} />
                    <span>{clinic.ownerEmail}</span>
                  </a>
                )}
              </span>
            </div>
          </div>
        </div>

        <QuickActionsBar
          clinic={clinic}
          crm={crm}
          busy={busy}
          onCopyId={handleCopyId}
          onExtendMonth={openExtendConfirm}
          onLogPayment={handleLogPayment}
          onToggleInactive={openInactiveConfirm}
          onDelete={openDeleteConfirm}
          onRefresh={refetch}
        />
      </header>

      {/* ============================================ */}
      {/* TABS + CONTENT */}
      {/* ============================================ */}
      <div className="ncd2-board">
        <div className="ncd2-tabs" role="tablist">
          {TABS.map((tab) => {
            const active = activeTab === tab.id;
            const showFlag = tab.id === "manage" && diffs.length > 0;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`ncd2-tab${active ? " active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={15} weight={active ? "fill" : "regular"} />
                <span className="ncd2-tab-label">{tab.label}</span>
                {showFlag && <OverrideFlag diffs={diffs} size={11} />}
              </button>
            );
          })}
        </div>

        <div className="ncd2-board-body">
          {activeTab === "details" && (
            <ClinicDetailsTab
              clinic={clinic}
              crm={crm}
              activity={activity}
              activityLoading={activityLoading}
              telemetry={telemetry}
              telemetryLoading={telemetryLoading}
              refetch={refetch}
              onCopyId={handleCopyId}
              onGoTab={setActiveTab}
            />
          )}
          {activeTab === "manage" && (
            <ManageTab
              clinic={clinic}
              crm={crm}
              therapistName={therapistName}
              showError={showError}
              focusPaymentToken={focusPaymentToken}
              refetch={refetch}
            />
          )}
          {activeTab === "lenny" && <LennyTab clinicId={clinicId} />}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.kind === "inactive") handleToggleInactive();
          if (confirm?.kind === "extend") handleExtendMonth(confirm.newDate);
        }}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        type={confirm?.type || "warning"}
      />

      <ConfirmDialog
        isOpen={deleteOpen}
        onClose={closeDeleteConfirm}
        onConfirm={handleDeleteClinic}
        title="מחיקת קליניקה לצמיתות"
        message={`פעולה זו תמחק את הקליניקה "${deleteTargetName || clinic.clinicId}" על כל המטופלים, הפגישות, הטיפולים, הקבצים והמטפלים שלה (כולל חשבונות ההתחברות). אין דרך לשחזר.`}
        confirmText={deleting ? "מוחק..." : "מחק לצמיתות"}
        type="danger"
        icon="delete"
        confirmDisabled={!deleteNameMatches || deleting}
      >
        <div className="ncd2-delete-confirm">
          <label className="ncd2-field">
            <span className="ncd2-field-label">
              לאישור, הקלד את שם הקליניקה: <strong dir="auto">{deleteTargetName || "(ללא שם — לא ניתן למחוק)"}</strong>
            </span>
            <input
              type="text"
              value={deleteTyped}
              onChange={(e) => setDeleteTyped(e.target.value)}
              placeholder="שם הקליניקה"
              autoComplete="off"
              disabled={deleting}
              autoFocus
            />
          </label>
        </div>
      </ConfirmDialog>
    </div>
  );
}

export default NivClinicDetailPage;
